import React, { useEffect, useState, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';

import { translate } from 'services/i18nService';
import SessionStorageService from 'services/SessionStorageService';
import ModalService from 'services/ModalService';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import Button from 'components/Button/Button';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import {
  BROADCAST_STATUS,
  NEW_SHOWS_STATE,
} from 'enum/videoPlayer';
import {
  generateID,
  isUserSpyTicketHolder,
  secondsToString,
} from 'utils/helpers';
import { MINUTE_AS_SECONDS } from 'enum/timeFormat';
import { SecondsTimer } from 'utils/timers';

import LoveLock from '../../../images/privateRoomLocked.png';

import Cam2CamControls from './Cam2CamControls';
import styles from './DesktopPrivateShowMenu.css';


const IDS = {
  prefix: 'GrnLnd20',
  element: 'DesktopPrivateShowMenu',
  timer: 'timer',
  addTime: 'addTime',
  endShow: 'endShow',
};
const makeId = generateID(IDS.prefix, IDS.element);

const secondsTimer = new SecondsTimer();

const DesktopPrivateShowMenu = (props) => {
  const { user, isSpyingShow, handlePrivateShowAction } = props;
  const [secondsCounter, setSecondsCounter] = useState(null);
  const {
    newShowsDetails,
    newShowsState,
    privateShowTimeRemain,
    privateShowBlocksOfTimeActive,
  } = FirebaseStore;
  const {
    privateShowReady,
    broadcastStatus,
    setPrivateShowEnded,
    setStreamHasEndedState,
    setSpyShowEnded,
  } = ProfileStore;
  const isAdmin = user.accessLevel >= ACCESS_LEVEL_NUM.ADMIN;

  const disconnect = useCallback(() => {
    if (isSpyingShow) {
      setSpyShowEnded(true);
    } else {
      setPrivateShowEnded(true);
      setSpyShowEnded(false);
    }
    setStreamHasEndedState(true);
  }, [isSpyingShow, setPrivateShowEnded, setSpyShowEnded, setStreamHasEndedState]);

  const updateTimer = useCallback((secondsToReconnect) => {
    setSecondsCounter(secondsToReconnect);

    if (secondsToReconnect < 0) {
      secondsTimer.stop();
      disconnect();
    }
  }, [disconnect]);

  useEffect(() => {
    const isSpying = broadcastStatus === BROADCAST_STATUS.SPYING;
    const username = user.username && user.username.toLowerCase();
    const notSpyTicketHolder = (
      (newShowsDetails && !newShowsDetails.spyTicketHolders) ||
      (
        newShowsDetails &&
        newShowsDetails.spyTicketHolders &&
        !isUserSpyTicketHolder(newShowsDetails.spyTicketHolders, username)
      )
    );

    if (newShowsDetails?.startTime && notSpyTicketHolder && !isSpying) {
      secondsTimer.start(updateTimer, privateShowTimeRemain, newShowsDetails.startTime);
    } else if (
      newShowsDetails?.startTime &&
      (
        (
          newShowsDetails &&
          newShowsDetails.spyTicketHolders &&
          isUserSpyTicketHolder(newShowsDetails.spyTicketHolders, username)
        ) ||
        (isAdmin && isSpying)
      )
    ) {
      if (isAdmin) {
        if (privateShowBlocksOfTimeActive === '1') {
          secondsTimer.start(updateTimer, privateShowTimeRemain, newShowsDetails.startTime);
        } else {
          secondsTimer.start(updateTimer, '', newShowsDetails.startTime);
        }
      } else {
        secondsTimer.start(updateTimer, '', newShowsDetails.spyTicketHolders[username]);
      }
    }

    return () => {
      secondsTimer.stop();
    };
  }, [
    broadcastStatus,
    isAdmin,
    newShowsDetails,
    privateShowBlocksOfTimeActive,
    privateShowTimeRemain,
    user.username,
    updateTimer,
  ]);

  useEffect(() => {
    if (newShowsState !== NEW_SHOWS_STATE.INSIDE_PS) {
      disconnect();
    }
  }, [newShowsState, disconnect]);

  useEffect(() => {
    if (privateShowReady && !SessionStorageService.get('privateShowTransitionDone')) {
      SessionStorageService.set('privateShowTransitionDone', true);
      SessionStorageService.set('wasUserInPrivateShow', true);
    }
  }, [privateShowReady]);

  const onAddTime = useCallback(() => {
    const modalProps = {
      handlePrivateShowAction,
      extendBlocksOfTime: true,
    };
    ModalService.showModal(
      ModalService.MODAL_KEYS.REQUEST_PRIVATE_SHOW_MODAL,
      modalProps
    );
  }, [handlePrivateShowAction]);

  const onOpenEndShowModal = useCallback(() => {
    const modalProps = {
      handlePrivateShowAction,
      isDesktop: true,
      isSpyingShow,
    };
    ModalService.showModal(
      ModalService.MODAL_KEYS.PRIVATE_SHOW_END_MODAL,
      modalProps
    );
  }, [handlePrivateShowAction, isSpyingShow]);

  const parsedSeconds = secondsToString(secondsCounter);
  const shouldBlink = (
    !isSpyingShow &&
    privateShowBlocksOfTimeActive === '1' &&
    secondsCounter &&
    secondsCounter <= MINUTE_AS_SECONDS
  );
  const showAddTimeButton = privateShowBlocksOfTimeActive === '1' && !isSpyingShow;
  const showPrivateShowMenuContent = (
    privateShowReady ||
    SessionStorageService.get('privateShowTransitionDone') ||
    isSpyingShow
  );

  const blinkingClass = classnames({ [styles.blinking]: shouldBlink });
  const menuWrapClass = classnames({
    [styles.menuWrap]: true,
    [styles.menuWrapTransition]: !showPrivateShowMenuContent,
  });

  return (
    <div className={menuWrapClass}>
      <img alt="love lock" className={styles.loveLogo} src={LoveLock} />
      {showPrivateShowMenuContent ? (
        <Fragment>
          <div className={styles.timer} id={makeId(IDS.timer)}>
            <QueryBuilderIcon />
            <span className={blinkingClass}>{parsedSeconds}</span>
          </div>
          {showAddTimeButton && (
            <Button
              className={styles.addTimeButton}
              icon={<AddCircleOutlineIcon className={blinkingClass} />}
              id={makeId(IDS.addTime)}
              label={<span className={blinkingClass}>{translate('v2profile.privateShow.addTime')}</span>}
              onClick={onAddTime}
            />
          )}
          {newShowsDetails?.cam2cam && !isSpyingShow && <Cam2CamControls />}
          <Button
            className={styles.endShowButton}
            icon={<HighlightOffIcon />}
            id={makeId(IDS.endShow)}
            label={<span>{translate('v2profile.button.endShow')}</span>}
            onClick={onOpenEndShowModal}
          />
        </Fragment>
      ) : (
        <p>{translate('v2profile.privateShow.entering')}</p>
      )}
    </div>
  );
};

DesktopPrivateShowMenu.propTypes = {
  handlePrivateShowAction: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  isSpyingShow: PropTypes.bool,
};

DesktopPrivateShowMenu.defaultProps = { isSpyingShow: false };

export default observer(DesktopPrivateShowMenu);
